import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, CreditCard, Receipt, FileText, User, LifeBuoy,
  LogOut, ExternalLink, CheckCircle2, AlertCircle, Clock, Download,
  Car, Sparkles, Wallet, AlertTriangle, ImagePlus, X, ShieldCheck, ShieldAlert, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { portalApi, PORTAL_API_BASE, PORTAL_TOKEN_KEY, formatPortalError } from "@/lib/portalApi";
import { usePortalAuth } from "@/lib/PortalAuthContext";
import Logo from "@/components/Logo";

const fmtAUD = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 }).format(n);

const STATUS_BADGE = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  due: "bg-white/10 text-white/60 border-white/20",
  overdue: "bg-red-500/15 text-red-400 border-red-500/30",
  waived: "bg-white/5 text-white/40 border-white/10",
  unpaid: "bg-white/10 text-white/60 border-white/20",
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-white/10 text-white/60 border-white/20",
  completed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  terminated: "bg-red-500/15 text-red-400 border-red-500/30",
};

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "tolls", label: "Tolls", icon: Receipt },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "damage", label: "Report Damage", icon: AlertTriangle },
  { id: "profile", label: "Profile", icon: User },
  { id: "support", label: "Support", icon: LifeBuoy },
];

export default function Portal() {
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout, refresh } = usePortalAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/dashboard");
      setDashboard(data);
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail, "Could not load your portal."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      toast.success("Payment received — thank you!");
    } else if (searchParams.get("cancelled") === "1") {
      toast("Payment cancelled.");
    }
  }, [searchParams]);

  const handleLogout = async () => {
    await logout();
    navigate("/portal/login");
  };

  const firstName = (user?.full_name || user?.email || "").split(" ")[0];

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right leading-tight">
              <div className="text-[10px] tracking-widest text-white/40">SIGNED IN</div>
              <div className="text-xs text-white">{user?.email}</div>
            </div>
            <button
              data-testid="portal-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
            ><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="text-[#D4AF37] tracking-[0.24em] text-[11px] mb-2">CUSTOMER PORTAL</div>
        <h1 className="font-display text-3xl md:text-4xl mb-1" style={{ fontWeight: 300 }}>
          Welcome back, <span className="italic text-[#D4AF37]">{firstName || "there"}</span>.
        </h1>
        <p className="text-white/50 text-sm mb-8">Manage your lease, payments, tolls and details.</p>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-1 border-b border-white/10 mb-8 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`portal-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm tracking-wide border-b-2 -mb-px transition whitespace-nowrap ${
                  tab === t.id ? "text-[#D4AF37] border-[#D4AF37]" : "text-white/50 border-transparent hover:text-white"
                }`}
              ><Icon size={14} /> {t.label}</button>
            );
          })}
        </div>

        {loading && <div className="text-white/40 text-sm">Loading…</div>}

        {!loading && dashboard && (
          <>
            {tab === "overview" && <OverviewTab dashboard={dashboard} onSwitch={setTab} />}
            {tab === "payments" && <PaymentsTab dashboard={dashboard} reload={loadDashboard} />}
            {tab === "tolls" && <TollsTab />}
            {tab === "documents" && <DocumentsTab dashboard={dashboard} />}
            {tab === "damage" && <DamageTab dashboard={dashboard} />}
            {tab === "profile" && <ProfileTab user={dashboard.customer} onUpdated={refresh} />}
            {tab === "support" && <SupportTab />}
          </>
        )}
      </main>
    </div>
  );
}

/* ------------- OVERVIEW ------------- */
function OverviewTab({ dashboard, onSwitch }) {
  const { leases, next_payment, tolls } = dashboard;

  return (
    <div data-testid="portal-overview" className="space-y-8">
      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={<Wallet size={16} />}
          label="NEXT PAYMENT"
          primary={next_payment ? fmtAUD(next_payment.amount) : "—"}
          secondary={next_payment ? `Due ${next_payment.due_date}` : "No upcoming payments"}
          status={next_payment?.status}
          cta={next_payment ? { label: "Pay now →", onClick: () => onSwitch("payments") } : null}
        />
        <KpiCard
          icon={<Receipt size={16} />}
          label="OUTSTANDING TOLLS"
          primary={fmtAUD(tolls.outstanding_amount)}
          secondary={`${tolls.outstanding_count} unpaid`}
          cta={tolls.outstanding_count > 0 ? { label: "Review tolls →", onClick: () => onSwitch("tolls") } : null}
        />
        <KpiCard
          icon={<Car size={16} />}
          label="ACTIVE LEASES"
          primary={leases.filter((l) => l.status === "active").length}
          secondary={`${leases.length} total on file`}
        />
      </div>

      {/* Leases list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white/80 tracking-wide text-sm">MY VEHICLE{leases.length !== 1 && "S"}</h2>
        </div>
        {leases.length === 0 ? (
          <EmptyState message="No leases on file yet." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {leases.map((l) => <LeaseCard key={l.id} lease={l} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, primary, secondary, status, cta }) {
  return (
    <div className="border border-white/10 rounded-lg p-5 bg-[#0a0a0a]">
      <div className="flex items-center gap-2 text-white/50 text-[10px] tracking-widest mb-3">
        <span className="text-[#D4AF37]">{icon}</span>{label}
      </div>
      <div className="font-display text-3xl mb-1 text-white" style={{ fontWeight: 300 }}>{primary}</div>
      <div className="flex items-center gap-2 text-xs text-white/50">
        <span>{secondary}</span>
        {status && (
          <span className={`px-1.5 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[status] || ""}`}>
            {status}
          </span>
        )}
      </div>
      {cta && (
        <button onClick={cta.onClick} className="mt-4 text-xs text-[#D4AF37] tracking-widest hover:opacity-80">
          {cta.label}
        </button>
      )}
    </div>
  );
}

function LeaseCard({ lease }) {
  const v = lease.vehicle || {};
  return (
    <div data-testid={`portal-lease-card-${lease.id}`} className="border border-white/10 rounded-lg overflow-hidden bg-[#0a0a0a]">
      {v.image_url ? (
        <img src={v.image_url} alt={`${v.year} ${v.make} ${v.model}`} className="w-full h-40 object-cover" />
      ) : (
        <div className="w-full h-40 bg-white/5 flex items-center justify-center text-white/30">
          <Car size={32} />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[10px] tracking-widest text-white/40">{lease.contract_number}</div>
            <div className="font-display text-xl text-white" style={{ fontWeight: 400 }}>
              {v.year} {v.make} {v.model}
            </div>
            <div className="text-xs text-white/50 mt-0.5">Rego {v.rego || "—"} · VIN {v.vin || "—"}</div>
          </div>
          <span className={`px-2 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[lease.status] || ""}`}>
            {lease.status}
          </span>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-white/60 mb-1.5">
            <span>{lease.paid_count} of {lease.total_count} payments</span>
            <span>{lease.progress_pct}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded overflow-hidden">
            <div className="h-full bg-[#D4AF37]" style={{ width: `${lease.progress_pct}%` }} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
          <div>
            <div className="text-white/40 text-[10px] tracking-widest">PAID TO DATE</div>
            <div className="text-white">{fmtAUD(lease.paid_to_date)}</div>
          </div>
          <div>
            <div className="text-white/40 text-[10px] tracking-widest">CONTRACT VALUE</div>
            <div className="text-white">{fmtAUD(lease.total_contract_value)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------- PAYMENTS ------------- */
function PaymentsTab({ dashboard, reload }) {
  const [selectedLease, setSelectedLease] = useState(dashboard.leases[0]?.id || null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const loadLease = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await portalApi.get(`/portal/leases/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLease(selectedLease); }, [selectedLease, loadLease]);

  const payNow = async (paymentId) => {
    setPayingId(paymentId);
    try {
      const { data } = await portalApi.post(`/portal/payments/${paymentId}/pay`);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail, "Could not start checkout."));
      setPayingId(null);
    }
  };

  if (dashboard.leases.length === 0) {
    return <EmptyState message="No leases yet — no payments to show." />;
  }

  return (
    <div data-testid="portal-payments" className="space-y-6">
      {dashboard.leases.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {dashboard.leases.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelectedLease(l.id)}
              className={`px-3 py-1.5 rounded text-xs border ${
                selectedLease === l.id
                  ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]"
                  : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              {l.contract_number}
            </button>
          ))}
        </div>
      )}

      {loading || !detail ? (
        <div className="text-white/40 text-sm">Loading payments…</div>
      ) : (
        <>
          {/* Early buyout card */}
          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[#D4AF37] text-[10px] tracking-widest mb-1">
                <Sparkles size={12} /> EARLY BUYOUT OFFER
              </div>
              <div className="text-white font-display text-2xl" style={{ fontWeight: 300 }}>
                Own it now for <span className="text-[#D4AF37]">{fmtAUD(detail.buyout.payoff_now)}</span>
              </div>
              <div className="text-xs text-white/50 mt-1">
                Save {fmtAUD(detail.buyout.discount)} vs {fmtAUD(detail.buyout.remaining_total)} across {detail.buyout.remaining_count} remaining payments.
              </div>
            </div>
            <a
              href={`mailto:admin@afmco.com.au?subject=Early buyout request — ${detail.lease.contract_number}&body=I would like to enquire about paying out my lease early.`}
              data-testid="portal-buyout-cta"
              className="bg-[#D4AF37] text-[#050505] text-xs font-semibold tracking-widest px-4 py-2.5 rounded hover:bg-[#E5C158] whitespace-nowrap"
            >REQUEST BUYOUT →</a>
          </div>

          {/* Payment schedule */}
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">DUE DATE</th>
                  <th className="text-right px-4 py-3">AMOUNT</th>
                  <th className="text-left px-4 py-3">STATUS</th>
                  <th className="text-right px-4 py-3">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {detail.payments.map((p) => (
                  <tr key={p.id} className="border-t border-white/10">
                    <td className="px-4 py-3 text-white/60">{p.sequence}</td>
                    <td className="px-4 py-3">{p.due_date}</td>
                    <td className="px-4 py-3 text-right">{fmtAUD(p.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[p.status] || ""}`}>
                        {p.status === "paid" && <CheckCircle2 size={11} />}
                        {p.status === "overdue" && <AlertCircle size={11} />}
                        {p.status === "due" && <Clock size={11} />}
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.status === "paid" ? (
                        <span className="text-xs text-white/40">{p.paid_date || "—"}</span>
                      ) : (
                        <button
                          onClick={() => payNow(p.id)}
                          disabled={payingId === p.id}
                          data-testid={`portal-pay-${p.id}`}
                          className="text-xs text-[#D4AF37] hover:opacity-80 tracking-widest inline-flex items-center gap-1 disabled:opacity-40"
                        >{payingId === p.id ? "OPENING…" : "PAY NOW"} <ExternalLink size={11} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------- TOLLS ------------- */
function TollsTab() {
  const [tolls, setTolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/tolls");
      setTolls(data);
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const payToll = async (id) => {
    setPayingId(id);
    try {
      const { data } = await portalApi.post(`/portal/tolls/${id}/pay`);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
      setPayingId(null);
    }
  };

  if (loading) return <div className="text-white/40 text-sm">Loading tolls…</div>;
  if (tolls.length === 0) return <EmptyState message="No tolls on file." />;

  return (
    <div data-testid="portal-tolls" className="border border-white/10 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
            <th className="text-left px-4 py-3">DATE</th>
            <th className="text-left px-4 py-3">PROVIDER</th>
            <th className="text-left px-4 py-3">LOCATION</th>
            <th className="text-left px-4 py-3">REGO</th>
            <th className="text-right px-4 py-3">TOLL</th>
            <th className="text-right px-4 py-3">FEE</th>
            <th className="text-right px-4 py-3">TOTAL</th>
            <th className="text-left px-4 py-3">STATUS</th>
            <th className="text-right px-4 py-3">ACTION</th>
          </tr>
        </thead>
        <tbody>
          {tolls.map((t) => (
            <tr key={t.id} className="border-t border-white/10">
              <td className="px-4 py-3">{t.event_date}</td>
              <td className="px-4 py-3 text-white/70">{t.provider}</td>
              <td className="px-4 py-3 text-white/70">{t.location || "—"}</td>
              <td className="px-4 py-3 text-white/70">{t.vehicle_rego}</td>
              <td className="px-4 py-3 text-right">{fmtAUD(t.toll_amount)}</td>
              <td className="px-4 py-3 text-right text-white/50">{fmtAUD(t.admin_fee)}</td>
              <td className="px-4 py-3 text-right text-white">{fmtAUD(t.total_amount)}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[t.status] || ""}`}>
                  {t.status}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {t.status === "paid" ? (
                  <span className="text-xs text-white/40">Paid</span>
                ) : (
                  <button
                    onClick={() => payToll(t.id)}
                    disabled={payingId === t.id}
                    data-testid={`portal-pay-toll-${t.id}`}
                    className="text-xs text-[#D4AF37] hover:opacity-80 tracking-widest inline-flex items-center gap-1 disabled:opacity-40"
                  >{payingId === t.id ? "OPENING…" : "PAY"} <ExternalLink size={11} /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------- DOCUMENTS ------------- */
function DocumentsTab({ dashboard }) {
  const download = async (leaseId, contractNo) => {
    try {
      const token = localStorage.getItem(PORTAL_TOKEN_KEY);
      const res = await fetch(`${PORTAL_API_BASE}/portal/leases/${leaseId}/agreement.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Lease-${contractNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Download failed.");
    }
  };

  if (dashboard.leases.length === 0) return <EmptyState message="No lease agreements available yet." />;

  return (
    <div data-testid="portal-documents" className="space-y-3">
      {dashboard.leases.map((l) => (
        <div key={l.id} className="flex items-center justify-between border border-white/10 rounded-lg p-4 bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] tracking-widest text-white/40">{l.contract_number}</div>
            <div className="text-white">{l.vehicle?.year} {l.vehicle?.make} {l.vehicle?.model} · Rego {l.vehicle?.rego || "—"}</div>
            <div className="text-xs text-white/40 mt-0.5">Start {l.start_date} · End {l.end_date}</div>
          </div>
          <button
            data-testid={`portal-download-lease-${l.id}`}
            onClick={() => download(l.id, l.contract_number)}
            className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:opacity-80 tracking-widest border border-[#D4AF37]/30 px-3 py-2 rounded"
          ><Download size={13} /> LEASE PDF</button>
        </div>
      ))}
    </div>
  );
}

/* ------------- PROFILE ------------- */
function ProfileTab({ user, onUpdated }) {
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [saving, setSaving] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [startingVerify, setStartingVerify] = useState(false);
  const [sharingLocation, setSharingLocation] = useState(false);

  useEffect(() => {
    portalApi.get("/portal/identity/status").then((r) => setIdentity(r.data)).catch(() => {});
  }, []);

  const startVerification = async () => {
    setStartingVerify(true);
    try {
      const { data } = await portalApi.post("/portal/identity/start");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail, "Could not start verification."));
      setStartingVerify(false);
    }
  };

  const shareLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported in this browser");
    setSharingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await portalApi.post("/portal/location", {
            lat: pos.coords.latitude, lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            heading: pos.coords.heading || null,
            speed: pos.coords.speed || null,
          });
          toast.success("Location shared");
        } catch (err) {
          toast.error(formatPortalError(err?.response?.data?.detail));
        } finally { setSharingLocation(false); }
      },
      (err) => {
        toast.error(err.message || "Could not read location");
        setSharingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await portalApi.patch("/portal/profile", { phone, address });
      toast.success("Profile updated");
      onUpdated?.();
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setPassword = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    if (pw1.length < 8) return toast.error("Password must be at least 8 characters");
    setPwSaving(true);
    try {
      await portalApi.post("/portal/auth/set-password", { new_password: pw1 });
      toast.success("Password updated");
      setPw1(""); setPw2("");
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally {
      setPwSaving(false);
    }
  };

  const idStatus = identity?.status || "not_started";
  const isVerified = idStatus === "verified";

  return (
    <div data-testid="portal-profile" className="space-y-6">
      {/* ID Verification card */}
      <div className={`border rounded-lg p-6 ${isVerified ? "border-emerald-500/30 bg-emerald-500/5" : "border-[#D4AF37]/30 bg-[#D4AF37]/5"}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className={`flex items-center gap-2 text-[10px] tracking-widest mb-1 ${isVerified ? "text-emerald-400" : "text-[#D4AF37]"}`}>
              {isVerified ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
              {isVerified ? "IDENTITY VERIFIED" : "IDENTITY VERIFICATION"}
            </div>
            <div className="text-white text-lg font-display" style={{ fontWeight: 400 }}>
              {isVerified ? "You're all set" : "Verify your driver's license"}
            </div>
            <div className="text-xs text-white/60 mt-1 max-w-md">
              {isVerified ? (
                <>Verified {identity?.verified_at?.slice(0, 10)} · Licence #{identity?.verified_outputs?.id_number || "—"}
                  {identity?.verified_outputs?.expiration_date ? ` · Expires ${identity.verified_outputs.expiration_date}` : ""}</>
              ) : (
                "We use Stripe Identity to securely verify your licence. Takes 2 minutes — you'll need your ID and a well-lit face for a selfie."
              )}
            </div>
          </div>
          {!isVerified && (
            <button
              onClick={startVerification}
              disabled={startingVerify}
              data-testid="portal-identity-start"
              className="bg-[#D4AF37] text-[#050505] text-xs font-semibold tracking-widest px-4 py-2.5 rounded hover:bg-[#E5C158] disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5"
            ><ShieldCheck size={12} /> {startingVerify ? "OPENING…" : idStatus === "requires_input" ? "TRY AGAIN" : "VERIFY NOW"}</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form onSubmit={save} className="border border-white/10 rounded-lg p-6 space-y-4 bg-[#0a0a0a]">
          <h2 className="text-white/80 tracking-wide text-sm mb-2">CONTACT DETAILS</h2>
          <ReadonlyField label="FULL NAME" value={user?.full_name} />
          <ReadonlyField label="EMAIL" value={user?.email} />
          <ReadonlyField label="ABN" value={user?.abn || "—"} />
          <Field label="PHONE" value={phone} onChange={setPhone} testid="portal-profile-phone" />
          <Field label="ADDRESS" value={address} onChange={setAddress} testid="portal-profile-address" />
          <button
            data-testid="portal-profile-save"
            disabled={saving}
            className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2.5 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
          >{saving ? "SAVING…" : "SAVE CHANGES"}</button>
        </form>

        <form onSubmit={setPassword} className="border border-white/10 rounded-lg p-6 space-y-4 bg-[#0a0a0a]">
          <h2 className="text-white/80 tracking-wide text-sm mb-2">SET / CHANGE PASSWORD</h2>
          <p className="text-xs text-white/50">Optional — use to sign in without waiting for a magic link.</p>
          <Field label="NEW PASSWORD" type="password" value={pw1} onChange={setPw1} testid="portal-profile-pw1" />
          <Field label="CONFIRM PASSWORD" type="password" value={pw2} onChange={setPw2} testid="portal-profile-pw2" />
          <button
            data-testid="portal-profile-save-pw"
            disabled={pwSaving}
            className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2.5 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
          >{pwSaving ? "SAVING…" : "UPDATE PASSWORD"}</button>

          <div className="border-t border-white/10 pt-4">
            <div className="text-[10px] tracking-widest text-white/50 mb-2">SHARE VEHICLE LOCATION</div>
            <p className="text-xs text-white/50 mb-3">One-tap check-in from your phone — helps our team support you faster.</p>
            <button
              type="button"
              onClick={shareLocation}
              disabled={sharingLocation}
              data-testid="portal-share-location"
              className="inline-flex items-center gap-1.5 border border-white/15 text-white text-xs tracking-widest px-4 py-2.5 rounded hover:border-[#D4AF37]/40 disabled:opacity-50"
            ><MapPin size={12} /> {sharingLocation ? "GETTING GPS…" : "SHARE MY LOCATION"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------- SUPPORT ------------- */
function SupportTab() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      await portalApi.post("/portal/support", { subject, message });
      setSent(true);
      setSubject(""); setMessage("");
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="portal-support" className="max-w-2xl">
      <form onSubmit={send} className="border border-white/10 rounded-lg p-6 space-y-4 bg-[#0a0a0a]">
        <h2 className="text-white/80 tracking-wide text-sm mb-1">SEND US A MESSAGE</h2>
        <p className="text-xs text-white/50 mb-4">
          Our team will reply to your registered email address within 1 business day.
        </p>
        <Field label="SUBJECT" value={subject} onChange={setSubject} testid="portal-support-subject" />
        <label className="block">
          <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">MESSAGE</span>
          <textarea
            data-testid="portal-support-message"
            value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/50"
            placeholder="How can we help?"
          />
        </label>
        <button
          data-testid="portal-support-send"
          disabled={sending}
          className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2.5 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
        >{sending ? "SENDING…" : "SEND MESSAGE"}</button>
        {sent && <div className="text-emerald-400 text-xs">Sent — we've got it.</div>}
      </form>
    </div>
  );
}

/* ------------- REPORT DAMAGE ------------- */
function DamageTab({ dashboard }) {
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/damage-reports");
      setPast(data);
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (dashboard.leases.length === 0) return <EmptyState message="No active lease — nothing to report damage on." />;

  return (
    <div data-testid="portal-damage" className="space-y-6">
      <div className="border border-orange-500/30 bg-orange-500/5 rounded-lg p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-orange-300 text-[10px] tracking-widest mb-1">
            <AlertTriangle size={12} /> DAMAGE REPORTING
          </div>
          <div className="text-white text-sm">Please report damage as soon as possible with clear photos.</div>
          <div className="text-xs text-white/50 mt-0.5">Our team will follow up within 1 business day.</div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="portal-damage-new"
          className="bg-[#D4AF37] text-[#050505] text-xs font-semibold tracking-widest px-4 py-2.5 rounded hover:bg-[#E5C158] whitespace-nowrap"
        >REPORT DAMAGE →</button>
      </div>

      <div>
        <h2 className="text-white/80 tracking-wide text-sm mb-3">MY REPORTS</h2>
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : past.length === 0 ? (
          <EmptyState message="You haven't reported any damage yet." />
        ) : (
          <div className="space-y-3">
            {past.map((p) => (
              <div key={p.id} className="border border-white/10 rounded-lg p-4 bg-[#0a0a0a]">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <div className="text-[10px] tracking-widest text-white/40">{p.date} · {p.vehicle_rego}</div>
                    <div className="text-white text-sm mt-1">{p.description}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[p.status] || "bg-white/10 border-white/20 text-white/60"}`}>
                    {(p.status || "").replace("_", " ")}
                  </span>
                </div>
                {p.photos?.length > 0 && (
                  <div className="text-xs text-white/40">{p.photos.length} photo{p.photos.length > 1 ? "s" : ""} attached</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <ReportDamageModal
          dashboard={dashboard}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function ReportDamageModal({ dashboard, onClose, onSubmitted }) {
  const [leaseId, setLeaseId] = useState(dashboard.leases[0]?.id || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [odometer, setOdometer] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim() || !date) return toast.error("Description and date required");
    setSending(true);
    const fd = new FormData();
    fd.append("date", date);
    fd.append("description", description);
    if (odometer) fd.append("odometer_km", odometer);
    if (leaseId) fd.append("lease_id", leaseId);
    files.forEach((f) => fd.append("photos", f));
    try {
      await portalApi.post("/portal/damage-reports", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Damage report submitted — we'll be in touch.");
      onSubmitted();
    } catch (err) {
      toast.error(formatPortalError(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
    >
      <div data-testid="portal-damage-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-lg my-4">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[#D4AF37] text-[10px] tracking-widest">DAMAGE REPORT</div>
            <div className="font-display text-xl">Report damage</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {dashboard.leases.length > 1 && (
            <label className="block">
              <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">VEHICLE</span>
              <select
                data-testid="portal-damage-vehicle"
                value={leaseId} onChange={(e) => setLeaseId(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              >
                {dashboard.leases.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.vehicle?.year} {l.vehicle?.make} {l.vehicle?.model} · {l.vehicle?.rego}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">DATE</span>
              <input
                data-testid="portal-damage-date"
                type="date" required value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">ODOMETER (KM)</span>
              <input
                data-testid="portal-damage-odometer"
                type="number" min="0" value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">WHAT HAPPENED?</span>
            <textarea
              data-testid="portal-damage-description"
              rows={4} required value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the damage, where and when it happened…"
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            />
          </label>

          <label className="block">
            <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">PHOTOS (max 10, up to 8 MB each)</span>
            <div className="border border-dashed border-white/15 rounded-lg p-4 flex items-center gap-2 text-white/60 hover:border-[#D4AF37]/40 cursor-pointer">
              <ImagePlus size={14} className="text-[#D4AF37]" />
              <span className="text-xs">
                {files.length > 0 ? `${files.length} file(s) selected` : "Tap to add photos of the damage"}
              </span>
              <input
                data-testid="portal-damage-photos"
                type="file" multiple accept="image/*"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))}
              />
            </div>
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest text-white/70 hover:text-white">CANCEL</button>
            <button
              type="submit"
              data-testid="portal-damage-submit"
              disabled={sending}
              className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
            >{sending ? "SUBMITTING…" : "SUBMIT REPORT"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------- Shared ------------- */
function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/50"
      />
    </label>
  );
}
function ReadonlyField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest text-white/50 mb-1.5">{label}</div>
      <div className="text-sm text-white/80 bg-white/[0.03] rounded px-3 py-2.5 border border-white/5">{value || "—"}</div>
    </div>
  );
}
function EmptyState({ message }) {
  return (
    <div className="border border-white/10 border-dashed rounded-lg p-10 text-center text-white/40 text-sm">
      {message}
    </div>
  );
}
